import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth, authFetch } from "../App";
import { toast } from "sonner";
import {
  format, addDays, subDays, startOfWeek, startOfMonth, endOfMonth, isToday,
  isTomorrow, parseISO, isSameDay, isSameMonth, addMonths, subMonths, getDay,
  getDaysInMonth, eachDayOfInterval, startOfYear, endOfYear, eachMonthOfInterval,
} from "date-fns";
import { de } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import {
  Plus, Mic, MicOff, X, MapPin, Users, FolderOpen,
  AlertTriangle, ChevronRight, ChevronLeft, Calendar, Trash2,
  Loader2, Zap, Share2, Lock, List, CalendarDays, CalendarClock,
  Users2, Grid3X3, Copy, LayoutGrid, ListChecks,
} from "lucide-react";
import TasksHub from "./TasksHub";

const API = "/api";
const HOUR_HEIGHT = 56;
const START_HOUR = 6;
const END_HOUR = 21;
const TOTAL_HOURS = END_HOUR - START_HOUR;
const HOURS = Array.from({ length: TOTAL_HOURS }, (_, i) => START_HOUR + i);
const DAY_LABELS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

function timeToMinutes(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
}
function minutesToTop(mins) { return ((mins - START_HOUR * 60) / 60) * HOUR_HEIGHT; }
function positionToTime(y) {
  const totalMins = START_HOUR * 60 + (y / HOUR_HEIGHT) * 60;
  const h = Math.floor(totalMins / 60);
  const m = Math.round((totalMins % 60) / 15) * 15;
  return `${String(h).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}
function formatTime(t) { return t || ""; }
function formatDate(d) {
  if (!d) return "";
  try {
    const parsed = parseISO(d);
    if (isToday(parsed)) return "Heute";
    if (isTomorrow(parsed)) return "Morgen";
    return format(parsed, "EEE, dd. MMM", { locale: de });
  } catch { return d; }
}
function getConflictIds(appts) {
  const ids = new Set();
  for (let i = 0; i < appts.length; i++) {
    for (let j = i + 1; j < appts.length; j++) {
      const a = appts[i], b = appts[j];
      if (!a.time || !b.time) continue;
      const aS = timeToMinutes(a.time), aE = timeToMinutes(a.end_time) || aS + 60;
      const bS = timeToMinutes(b.time), bE = timeToMinutes(b.end_time) || bS + 60;
      if (aS < bE && bS < aE) { ids.add(a.id); ids.add(b.id); }
    }
  }
  return ids;
}

// ==================== MAIN COMPONENT ====================
export default function Terminplaner() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    // BUGFIX: Default-Ansicht auf Aufgaben (User-Wunsch). Persistiert im LocalStorage,
    // damit jeder User seine zuletzt genutzte Ansicht wieder findet.
    try { return localStorage.getItem("terminplaner.activeTab") || "tasks"; } catch { return "tasks"; }
  });
  const [appointments, setAppointments] = useState([]);
  const [teamData, setTeamData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editAppt, setEditAppt] = useState(null);
  const [projects, setProjects] = useState([]);
  const [prefillData, setPrefillData] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  // Task integration (Aufgaben im Terminplaner)
  const [showQuickTask, setShowQuickTask] = useState(false);
  const [taskFilter, setTaskFilter] = useState({ mineOnly: false, categories: [] });
  const [pendingTaskLink, setPendingTaskLink] = useState(null); // { task, onLinked }

  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 1 }), [currentDate]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const monthStart = useMemo(() => startOfMonth(currentDate), [currentDate]);

  useEffect(() => {
    if (user && !["admin", "projektleiter"].includes(user.role)) navigate("/app");
  }, [user, navigate]);

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    try {
      // Determine date range for combined fetch
      let rangeFrom = format(currentDate, "yyyy-MM-dd");
      let rangeTo = rangeFrom;
      if (activeTab === "week" || activeTab === "team") {
        rangeFrom = format(weekStart, "yyyy-MM-dd");
        rangeTo = format(addDays(weekStart, 6), "yyyy-MM-dd");
      } else if (activeTab === "month") {
        rangeFrom = format(monthStart, "yyyy-MM-dd");
        rangeTo = format(endOfMonth(currentDate), "yyyy-MM-dd");
      } else if (activeTab === "year") {
        rangeFrom = format(startOfYear(currentDate), "yyyy-MM-dd");
        rangeTo = format(endOfYear(currentDate), "yyyy-MM-dd");
      }

      // Fetch tasks with due_date in range (calendar integration)
      const tasksP = authFetch(`${API}/calendar/tasks?start=${rangeFrom}&end=${rangeTo}`)
        .then(r => r.ok ? r.json() : { tasks: [] })
        .catch(() => ({ tasks: [] }));

      if (activeTab === "team") {
        const res = await authFetch(`${API}/appointments/team?date_from=${rangeFrom}&date_to=${rangeTo}`);
        if (res.ok) setTeamData(await res.json());
        await tasksP; // warm fetch, not rendered in team view
      } else {
        let url;
        if (activeTab === "week") {
          url = `${API}/appointments?date_from=${rangeFrom}&date_to=${rangeTo}`;
        } else if (activeTab === "day") {
          url = `${API}/appointments?date_from=${rangeFrom}&date_to=${rangeFrom}`;
        } else if (activeTab === "month") {
          url = `${API}/appointments?date_from=${rangeFrom}&date_to=${rangeTo}`;
        } else if (activeTab === "year") {
          url = `${API}/appointments?date_from=${rangeFrom}&date_to=${rangeTo}`;
        } else {
          url = `${API}/appointments?view=today`;
        }
        const [res, tasksData] = await Promise.all([authFetch(url), tasksP]);
        const appts = res.ok ? await res.json() : [];

        // Merge tasks as virtual appointments (all-day, yellow/quick style)
        const userId = JSON.parse(localStorage.getItem("user") || "{}")?.id;
        const taskAppts = (tasksData.tasks || [])
          .filter(t => t.status !== "erledigt")
          // Filter: Meine Aufgaben
          .filter(t => !taskFilter.mineOnly || t.assigned_to === userId || (t.assigned_users || []).includes(userId))
          // Filter: Kategorien (wenn keine ausgewaehlt → alle anzeigen)
          .filter(t => taskFilter.categories.length === 0 || taskFilter.categories.includes(t.category))
          .map(t => ({
            id: `task_${t.id}`,
            _raw_task_id: t.id,
            _raw_project_id: t.project_id,
            is_task: true,
            is_quick: true,
            title: `${t.task_type === "PERSONAL" ? "[Privat] " : ""}${t.title}`,
            date: (t.due_date || "").slice(0, 10),
            time: null,
            end_time: null,
            project_name: t.task_type === "PROJECT" ? "Aufgabe" : "Persönlich",
            priority: t.priority,
            category: t.category,
            created_by: t.assigned_to,
            visibility: "private",
          }));
        setAppointments([...appts, ...taskAppts]);
      }
    } catch { toast.error("Fehler beim Laden"); }
    finally { setLoading(false); }
  }, [activeTab, currentDate, weekStart, monthStart, taskFilter]);

  useEffect(() => { loadAppointments(); }, [loadAppointments]);
  // Auto-refresh appointments every 60s
  useEffect(() => {
    const interval = setInterval(() => { loadAppointments(); }, 60000);
    return () => clearInterval(interval);
  }, [loadAppointments]);
  useEffect(() => {
    authFetch(`${API}/projects?status=aktiv`).then(r => r.ok ? r.json() : []).then(setProjects).catch(() => {});
  }, []);

  const handleDelete = async (id) => {
    if (!confirm("Termin wirklich loeschen?")) return;
    const res = await authFetch(`${API}/appointments/${id}`, { method: "DELETE" });
    if (res.ok) { toast.success("Geloescht"); loadAppointments(); } else toast.error("Fehler");
  };
  const handleEdit = (appt) => {
    // Task virtual appointment → navigate to task list
    if (appt?.is_task) {
      if (appt._raw_project_id) {
        navigate(`/app/projekte/${appt._raw_project_id}/aufgaben`);
      } else if (user?.role === "admin") {
        navigate("/app/aufgaben");
      } else {
        navigate("/app/m/meine-aufgaben");
      }
      return;
    }
    setEditAppt(appt); setPrefillData(null); setShowForm(true);
  };
  const handleNewAppt = (prefill = null) => { setEditAppt(null); setPrefillData(prefill); setShowForm(true); };
  const handleFormClose = () => { setShowForm(false); setEditAppt(null); setPrefillData(null); setPendingTaskLink(null); };
  const handleFormSave = () => { handleFormClose(); loadAppointments(); };
  const handleDragUpdate = async (apptId, newDate, newTime) => {
    try {
      const payload = { date: newDate };
      if (newTime) payload.time = newTime;
      const res = await authFetch(`${API}/appointments/${apptId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      if (res.ok) { toast.success("Verschoben"); loadAppointments(); }
      else { const e = await res.json(); toast.error(e.detail || "Fehler"); }
    } catch { toast.error("Fehler"); }
  };

  const handleDragCopy = async (apptId, newDate, newTime) => {
    try {
      const payload = { date: newDate };
      if (newTime) payload.time = newTime;
      const res = await authFetch(`${API}/appointments/${apptId}/duplicate`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload),
      });
      if (res.ok) { toast.success("Termin kopiert"); loadAppointments(); }
      else { const e = await res.json(); toast.error(e.detail || "Fehler"); }
    } catch { toast.error("Fehler beim Kopieren"); }
  };

  const goToday = () => setCurrentDate(new Date());
  const goPrev = () => {
    if (activeTab === "month") setCurrentDate(d => subMonths(d, 1));
    else if (activeTab === "year") setCurrentDate(d => subMonths(d, 12));
    else if (activeTab === "week" || activeTab === "team") setCurrentDate(d => subDays(d, 7));
    else setCurrentDate(d => subDays(d, 1));
  };
  const goNext = () => {
    if (activeTab === "month") setCurrentDate(d => addMonths(d, 1));
    else if (activeTab === "year") setCurrentDate(d => addMonths(d, 12));
    else if (activeTab === "week" || activeTab === "team") setCurrentDate(d => addDays(d, 7));
    else setCurrentDate(d => addDays(d, 1));
  };

  const getSubtitle = () => {
    if (activeTab === "week" || activeTab === "team") return `${format(weekStart, "d. MMM", { locale: de })} - ${format(addDays(weekStart, 6), "d. MMM yyyy", { locale: de })}`;
    if (activeTab === "day") return format(currentDate, "EEEE, d. MMMM yyyy", { locale: de });
    if (activeTab === "month") return format(currentDate, "MMMM yyyy", { locale: de });
    if (activeTab === "year") return format(currentDate, "yyyy");
    return "Heutige Termine";
  };

  const TABS = [
    { key: "today", label: "Heute", icon: List },
    { key: "day", label: "Tag", icon: CalendarClock },
    { key: "week", label: "Woche", icon: CalendarDays },
    { key: "month", label: "Monat", icon: Grid3X3 },
    { key: "year", label: "Jahr", icon: LayoutGrid },
    { key: "team", label: "Team", icon: Users2 },
    { key: "tasks", label: "Aufgaben", icon: ListChecks },
  ];

  // Linking task → event (from TasksHub).
  // Opens the AppointmentForm prefilled with task.title; on save, parent links event_id back.
  const handleTaskLinkToEvent = (task, onLinked) => {
    setPendingTaskLink({ task, onLinked });
    setEditAppt(null);
    setPrefillData({
      title: task.title,
      date: task.due_date ? task.due_date.slice(0, 10) : format(new Date(), "yyyy-MM-dd"),
      project_id: task.project_id || "",
      note: task.description || "",
      _linked_task_id: task.id,
    });
    setShowForm(true);
  };
  const handleFormSaveWithLink = (createdEventId) => {
    if (pendingTaskLink && createdEventId && pendingTaskLink.onLinked) {
      try { pendingTaskLink.onLinked(createdEventId); } catch { /* ignore */ }
    }
    setPendingTaskLink(null);
    handleFormClose();
    loadAppointments();
  };

  return (
    <div className="h-[calc(100vh-80px)] flex flex-col">
      <div className="flex-shrink-0 flex items-center justify-between mb-3">
        <div>
          <h1 className="text-lg font-bold" style={{ color: "var(--tga-text-bright)" }} data-testid="terminplaner-title">Terminplaner</h1>
          <p className="text-[10px]" style={{ color: "var(--tga-text-muted)" }}>{getSubtitle()}</p>
        </div>
        <div className="flex items-center gap-2">
          <VoiceButton onResult={loadAppointments} />
          <button onClick={() => setShowQuickTask(true)} className="flex items-center gap-1.5 px-3 py-2 bg-amber-600 text-white text-[13px] font-medium rounded-lg hover:bg-amber-700 transition-colors" data-testid="new-task-btn">
            <Plus className="w-4 h-4" />Aufgabe
          </button>
          <button onClick={() => handleNewAppt()} className="flex items-center gap-1.5 px-3 py-2 bg-slate-800 text-white text-[13px] font-medium rounded-lg hover:bg-slate-900 transition-colors" data-testid="new-appointment-btn">
            <Plus className="w-4 h-4" />Termin
          </button>
        </div>
      </div>

      {/* Aufgaben-Filter-Bar (nur Kalender-Views — TasksHub hat eigene Filter) */}
      {activeTab !== "tasks" && (
      <div className="flex-shrink-0 flex flex-wrap items-center gap-2 mb-2 px-1" data-testid="task-filter-bar">
        <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--tga-text-secondary)" }}>Aufgaben:</span>
        <button onClick={() => setTaskFilter(p => ({ ...p, mineOnly: !p.mineOnly }))}
          className={`px-2.5 py-1 rounded text-[11px] font-semibold border transition-colors ${taskFilter.mineOnly ? "bg-blue-500/15 border-blue-500/30 text-blue-300" : "border-transparent"}`}
          style={!taskFilter.mineOnly ? { color: "var(--tga-text-secondary)", background: "var(--tga-bg-hover-soft)" } : {}}
          data-testid="filter-mine-only">
          Meine Aufgaben
        </button>
        {[{k:"projekt",l:"Projekt"},{k:"buero",l:"Büro"},{k:"privat",l:"Privat"}].map(c => {
          const on = taskFilter.categories.includes(c.k);
          return (
            <button key={c.k}
              onClick={() => setTaskFilter(p => ({ ...p, categories: on ? p.categories.filter(x => x !== c.k) : [...p.categories, c.k] }))}
              className={`px-2.5 py-1 rounded text-[11px] font-semibold border transition-colors ${on ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300" : "border-transparent"}`}
              style={!on ? { color: "var(--tga-text-secondary)", background: "var(--tga-bg-hover-soft)" } : {}}
              data-testid={`filter-cat-${c.k}`}>
              {c.l}
            </button>
          );
        })}
        {(taskFilter.mineOnly || taskFilter.categories.length > 0) && (
          <button onClick={() => setTaskFilter({ mineOnly: false, categories: [] })}
            className="px-2 py-1 text-[10px] underline" style={{ color: "var(--tga-text-dim)" }}
            data-testid="filter-clear">Filter zuruecksetzen</button>
        )}
      </div>
      )}

      <div className="flex-shrink-0 flex items-center justify-between mb-2">
        <div className="flex gap-0.5 p-0.5 rounded-lg"
             style={{ background: "var(--tga-bg-hover-soft)", border: "1px solid var(--tga-border)" }}
             data-testid="view-tabs">
          {TABS.map(t => (
            <button key={t.key} onClick={() => { setActiveTab(t.key); try { localStorage.setItem("terminplaner.activeTab", t.key); } catch {} }}
              className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-medium rounded-md transition-all"
              style={activeTab === t.key
                ? { background: "var(--tga-bg-card)", color: "var(--tga-text-bright)", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }
                : { color: "var(--tga-text-secondary)", background: "transparent" }}
              data-testid={`tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5" />{t.label}
            </button>
          ))}
        </div>
        {activeTab !== "today" && activeTab !== "tasks" && (
          <div className="flex items-center gap-1">
            <button onClick={goPrev} className="p-1.5 rounded transition-colors"
              style={{ color: "var(--tga-text-secondary)" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "var(--tga-bg-hover-soft)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              data-testid="nav-prev"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={goToday} className="px-2 py-1 text-[11px] font-medium rounded transition-colors"
              style={{ color: "var(--tga-sb-text-active)" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "var(--tga-sb-active-bg)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              data-testid="nav-today">Heute</button>
            <button onClick={goNext} className="p-1.5 rounded transition-colors"
              style={{ color: "var(--tga-text-secondary)" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "var(--tga-bg-hover-soft)"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
              data-testid="nav-next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-hidden">
        {loading && activeTab !== "tasks" ? (
          <div className="h-full flex items-center justify-center"><Loader2 className="w-6 h-6 text-gray-400 animate-spin" /></div>
        ) : activeTab === "tasks" ? (
          <TasksHub projects={projects} onLinkToEvent={handleTaskLinkToEvent} />
        ) : activeTab === "today" ? (
          <TodayListView appointments={appointments} userId={user?.id} onEdit={handleEdit} onDelete={handleDelete} onNewAppt={handleNewAppt} />
        ) : activeTab === "day" ? (
          <DayCalendarView date={currentDate} appointments={appointments} userId={user?.id} onEdit={handleEdit} onDragUpdate={handleDragUpdate} onDragCopy={handleDragCopy} onNewAppt={handleNewAppt} />
        ) : activeTab === "month" ? (
          <MonthCalendarView currentDate={currentDate} appointments={appointments} userId={user?.id} onEdit={handleEdit} onNewAppt={handleNewAppt} onDayClick={(d) => { setCurrentDate(d); setActiveTab("day"); }} />
        ) : activeTab === "year" ? (
          <YearCalendarView currentDate={currentDate} appointments={appointments} onDayClick={(d) => { setCurrentDate(d); setActiveTab("day"); }} onMonthClick={(d) => { setCurrentDate(d); setActiveTab("month"); }} />
        ) : activeTab === "team" ? (
          <TeamWeekView weekDays={weekDays} teamData={teamData} userId={user?.id} onEdit={handleEdit} onDragUpdate={handleDragUpdate} onDragCopy={handleDragCopy} onNewAppt={handleNewAppt} />
        ) : (
          <WeekCalendarView weekDays={weekDays} appointments={appointments} userId={user?.id} onEdit={handleEdit} onDragUpdate={handleDragUpdate} onDragCopy={handleDragCopy} onNewAppt={handleNewAppt} />
        )}
      </div>

      {showForm && (
        <AppointmentForm appointment={editAppt} projects={projects} prefillData={prefillData} onClose={handleFormClose} onSave={pendingTaskLink ? handleFormSaveWithLink : handleFormSave} />
      )}
      {showQuickTask && (
        <QuickTaskModal
          projects={projects}
          onClose={() => setShowQuickTask(false)}
          onSaved={() => { setShowQuickTask(false); loadAppointments(); }}
        />
      )}
    </div>
  );
}

// ==================== TODAY LIST VIEW ====================
function TodayListView({ appointments, userId, onEdit, onDelete, onNewAppt }) {
  const grouped = {};
  appointments.forEach(a => { const k = a.date || "x"; if (!grouped[k]) grouped[k] = []; grouped[k].push(a); });
  if (!appointments.length) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <Calendar className="w-10 h-10 text-gray-300 mb-3" />
        <p className="text-sm text-gray-400">Keine Termine heute</p>
        <button onClick={() => onNewAppt()} className="mt-3 text-sm text-blue-600 font-medium">+ Termin erstellen</button>
      </div>
    );
  }
  return (
    <div className="h-full overflow-y-auto space-y-4 max-w-3xl mx-auto" data-testid="appointment-list">
      {Object.keys(grouped).sort().map(dk => (
        <div key={dk}>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-2 px-1">{formatDate(dk)}</h3>
          <div className="space-y-2">
            {grouped[dk].map(a => <AppointmentCard key={a.id} appt={a} userId={userId} onEdit={() => onEdit(a)} onDelete={() => onDelete(a.id)} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ==================== TIME GRID COLUMN (shared) ====================
function TimeGridColumn({ dateStr, appts, userId, onEdit, onDragUpdate, onDragCopy, onNewAppt, scrollRef, isTeam }) {
  const dayAppts = appts.filter(a => a.date === dateStr && a.time);
  const quickAppts = appts.filter(a => a.date === dateStr && !a.time);
  const conflictIds = getConflictIds(dayAppts);
  const today = isToday(parseISO(dateStr));

  const handleDrop = (e) => {
    e.preventDefault();
    const apptId = e.dataTransfer.getData("text/plain");
    if (!apptId) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top + (scrollRef?.current?.scrollTop || 0);
    const newTime = positionToTime(y);
    if (e.ctrlKey || e.altKey || e.metaKey) {
      onDragCopy?.(apptId, dateStr, newTime);
    } else {
      onDragUpdate(apptId, dateStr, newTime);
    }
  };

  const handleClick = (e) => {
    if (e.target === e.currentTarget || e.target.classList.contains("hour-line")) {
      const rect = e.currentTarget.getBoundingClientRect();
      const y = e.clientY - rect.top + (scrollRef?.current?.scrollTop || 0);
      onNewAppt({ date: dateStr, time: positionToTime(y) });
    }
  };

  return (
    <div className={`relative border-l border-gray-200 ${today ? "bg-blue-50/30" : ""}`}
      style={{ flex: 1 }}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = (e.ctrlKey || e.altKey || e.metaKey) ? "copy" : "move"; }}
      onDrop={handleDrop} onClick={handleClick}>
      {HOURS.map(h => <div key={h} className="hour-line absolute left-0 right-0 border-t border-gray-100" style={{ top: (h - START_HOUR) * HOUR_HEIGHT, height: HOUR_HEIGHT }} />)}
      {HOURS.map(h => <div key={`h${h}`} className="hour-line absolute left-0 right-0 border-t border-gray-50" style={{ top: (h - START_HOUR) * HOUR_HEIGHT + HOUR_HEIGHT / 2 }} />)}

      {today && (() => {
        const now = new Date();
        const nm = now.getHours() * 60 + now.getMinutes();
        return nm >= START_HOUR * 60 && nm <= END_HOUR * 60 ? (
          <div className="absolute left-0 right-0 border-t-2 border-red-500 z-20 pointer-events-none" style={{ top: minutesToTop(nm) }}>
            <div className="absolute -left-1 -top-[5px] w-2.5 h-2.5 bg-red-500 rounded-full" />
          </div>
        ) : null;
      })()}

      {quickAppts.length > 0 && (
        <div className="absolute top-0 left-0.5 right-0.5 z-10">
          {quickAppts.map(a => (
            <div key={a.id} onClick={(e) => { e.stopPropagation(); onEdit(a); }}
              className="mb-0.5 px-1 py-0.5 bg-amber-100 border border-amber-300 text-[8px] text-amber-800 font-medium truncate rounded cursor-pointer hover:bg-amber-200">
              <Zap className="w-2 h-2 inline mr-0.5" />{a.title}
            </div>
          ))}
        </div>
      )}

      {dayAppts.map(appt => {
        const startMins = timeToMinutes(appt.time);
        const endMins = timeToMinutes(appt.end_time) || startMins + 60;
        const top = minutesToTop(startMins);
        const height = Math.max(((endMins - startMins) / 60) * HOUR_HEIGHT, 22);
        const isConflict = conflictIds.has(appt.id);
        const isShared = appt.visibility === "geteilt";
        const isOwn = appt.created_by === userId;
        const compact = isTeam;

        return (
          <div key={appt.id}
            draggable={isOwn}
            onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", appt.id); }}
            onClick={(e) => { e.stopPropagation(); onEdit(appt); }}
            className={`absolute left-0.5 right-0.5 z-10 rounded px-1 py-0.5 cursor-pointer transition-shadow hover:shadow-lg overflow-hidden ${
              isConflict ? "bg-red-100 border-2 border-red-400" : isShared ? "bg-blue-100 border border-blue-300" : "bg-slate-100 border border-slate-300"
            }`}
            style={{ top, height, minHeight: 22 }}
            data-testid={`cal-appt-${appt.id}`}
          >
            {isConflict && <AlertTriangle className="absolute top-0 right-0 w-2.5 h-2.5 text-red-500" />}
            <div className={`font-bold text-gray-700 leading-tight ${compact ? "text-[7px]" : "text-[9px]"}`}>
              {formatTime(appt.time)}{appt.end_time ? `-${formatTime(appt.end_time)}` : ""}
            </div>
            <div className={`font-semibold text-gray-900 truncate leading-tight ${compact ? "text-[8px]" : "text-[10px]"}`}>{appt.title}</div>
            {!compact && height > 40 && appt.project_name && <div className="text-[8px] text-gray-500 truncate">{appt.project_name}</div>}
            {!compact && height > 52 && appt.location && <div className="text-[8px] text-gray-400 truncate flex items-center gap-0.5"><MapPin className="w-2 h-2" />{appt.location}</div>}
            {isShared && <Share2 className={`absolute bottom-0 right-0 text-blue-400 ${compact ? "w-2 h-2" : "w-2.5 h-2.5"}`} />}
          </div>
        );
      })}
    </div>
  );
}

// ==================== WEEK CALENDAR VIEW ====================
function WeekCalendarView({ weekDays, appointments, userId, onEdit, onDragUpdate, onDragCopy, onNewAppt }) {
  const scrollRef = useRef(null);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = (7 - START_HOUR) * HOUR_HEIGHT; }, []);

  return (
    <div className="h-full flex flex-col border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid="week-calendar">
      <div className="flex-shrink-0 flex border-b border-gray-200 bg-gray-50">
        <div className="w-14 flex-shrink-0" />
        {weekDays.map((day, i) => {
          const td = isToday(day);
          return (
            <div key={i} className={`flex-1 text-center py-2 border-l border-gray-200 ${td ? "bg-blue-50" : ""}`}>
              <div className="text-[10px] font-medium text-gray-500 uppercase">{DAY_LABELS[i]}</div>
              <div className={`text-[14px] font-bold ${td ? "text-blue-600" : "text-gray-800"}`}>{format(day, "d")}</div>
            </div>
          );
        })}
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="flex relative" style={{ height: TOTAL_HOURS * HOUR_HEIGHT }}>
          <div className="w-14 flex-shrink-0 relative">
            {HOURS.map(h => (
              <div key={h} className="absolute left-0 right-0 border-t border-gray-100 text-[10px] text-gray-400 pr-2 text-right" style={{ top: (h - START_HOUR) * HOUR_HEIGHT }}>
                <span className="relative -top-[7px]">{`${String(h).padStart(2, "0")}:00`}</span>
              </div>
            ))}
          </div>
          {weekDays.map((day, i) => (
            <TimeGridColumn key={i} dateStr={format(day, "yyyy-MM-dd")} appts={appointments} userId={userId}
              onEdit={onEdit} onDragUpdate={onDragUpdate} onDragCopy={onDragCopy} onNewAppt={onNewAppt} scrollRef={scrollRef} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ==================== DAY CALENDAR VIEW ====================
function DayCalendarView({ date, appointments, userId, onEdit, onDragUpdate, onDragCopy, onNewAppt }) {
  const scrollRef = useRef(null);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = (7 - START_HOUR) * HOUR_HEIGHT; }, []);
  const dateStr = format(date, "yyyy-MM-dd");
  const quickAppts = appointments.filter(a => a.date === dateStr && !a.time);

  return (
    <div className="h-full flex flex-col border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid="day-calendar">
      {quickAppts.length > 0 && (
        <div className="flex-shrink-0 border-b border-gray-200 p-2 bg-amber-50/50">
          <div className="text-[10px] font-semibold text-amber-700 mb-1 flex items-center gap-1"><Zap className="w-3 h-3" />Schnell-Termine</div>
          <div className="flex flex-wrap gap-1.5">
            {quickAppts.map(a => (
              <button key={a.id} onClick={() => onEdit(a)} className="px-2 py-1 bg-amber-100 border border-amber-300 text-[11px] font-medium text-amber-800 rounded hover:bg-amber-200" data-testid={`quick-appt-${a.id}`}>{a.title}</button>
            ))}
          </div>
        </div>
      )}
      <div className={`flex-shrink-0 text-center py-2 border-b border-gray-200 ${isToday(date) ? "bg-blue-50" : "bg-gray-50"}`}>
        <span className={`text-[14px] font-bold ${isToday(date) ? "text-blue-600" : "text-gray-800"}`}>{format(date, "EEEE, d. MMMM yyyy", { locale: de })}</span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        <div className="flex relative" style={{ height: TOTAL_HOURS * HOUR_HEIGHT }}>
          <div className="w-16 flex-shrink-0 relative">
            {HOURS.map(h => (
              <div key={h} className="absolute left-0 right-0 border-t border-gray-100 text-[11px] text-gray-400 pr-2 text-right" style={{ top: (h - START_HOUR) * HOUR_HEIGHT }}>
                <span className="relative -top-[8px]">{`${String(h).padStart(2, "0")}:00`}</span>
              </div>
            ))}
          </div>
          <TimeGridColumn dateStr={dateStr} appts={appointments} userId={userId}
            onEdit={onEdit} onDragUpdate={onDragUpdate} onDragCopy={onDragCopy} onNewAppt={onNewAppt} scrollRef={scrollRef} />
        </div>
      </div>
    </div>
  );
}

// ==================== MONTH CALENDAR VIEW ====================
function MonthCalendarView({ currentDate, appointments, userId, onEdit, onNewAppt, onDayClick }) {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Pad start to Monday
  const firstDayOfWeek = (getDay(monthStart) + 6) % 7; // 0=Mon
  const padStart = Array.from({ length: firstDayOfWeek }, (_, i) => subDays(monthStart, firstDayOfWeek - i));
  // Pad end to Sunday
  const lastDayOfWeek = (getDay(monthEnd) + 6) % 7;
  const padEnd = lastDayOfWeek < 6 ? Array.from({ length: 6 - lastDayOfWeek }, (_, i) => addDays(monthEnd, i + 1)) : [];
  const allDays = [...padStart, ...days, ...padEnd];

  const apptsByDate = {};
  appointments.forEach(a => {
    if (!apptsByDate[a.date]) apptsByDate[a.date] = [];
    apptsByDate[a.date].push(a);
  });

  return (
    <div className="h-full flex flex-col border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid="month-calendar">
      <div className="flex-shrink-0 grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {DAY_LABELS.map(d => (
          <div key={d} className="text-center py-2 text-[10px] font-semibold text-gray-500 uppercase border-l border-gray-200 first:border-l-0">{d}</div>
        ))}
      </div>
      <div className="flex-1 overflow-y-auto">
        <div className="grid grid-cols-7 auto-rows-fr" style={{ minHeight: "100%" }}>
          {allDays.map((day, i) => {
            const ds = format(day, "yyyy-MM-dd");
            const dayAppts = apptsByDate[ds] || [];
            const inMonth = isSameMonth(day, currentDate);
            const td = isToday(day);
            const conflicts = getConflictIds(dayAppts.filter(a => a.time));
            const hasConflict = conflicts.size > 0;

            return (
              <div key={i}
                className={`border-l border-b border-gray-200 first:border-l-0 p-1 min-h-[80px] cursor-pointer hover:bg-gray-50 transition-colors ${!inMonth ? "bg-gray-50/50" : ""}`}
                onClick={() => onDayClick(day)}
                data-testid={`month-day-${ds}`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className={`text-[11px] font-bold w-6 h-6 flex items-center justify-center rounded-full ${td ? "bg-blue-600 text-white" : inMonth ? "text-gray-800" : "text-gray-300"}`}>{format(day, "d")}</span>
                  {hasConflict && <AlertTriangle className="w-3 h-3 text-red-400" />}
                </div>
                <div className="space-y-0.5">
                  {dayAppts.slice(0, 3).map(a => {
                    const isShared = a.visibility === "geteilt";
                    const isQuick = a.is_quick && !a.time;
                    return (
                      <div key={a.id}
                        onClick={(e) => { e.stopPropagation(); onEdit(a); }}
                        className={`text-[8px] font-medium px-1 py-0.5 rounded truncate cursor-pointer ${
                          isQuick ? "bg-amber-100 text-amber-800"
                          : conflicts.has(a.id) ? "bg-red-100 text-red-800"
                          : isShared ? "bg-blue-100 text-blue-800"
                          : "bg-slate-100 text-slate-700"
                        }`}
                        data-testid={`month-appt-${a.id}`}
                      >
                        {a.time && <span className="font-bold mr-0.5">{a.time}</span>}{a.title}
                      </div>
                    );
                  })}
                  {dayAppts.length > 3 && (
                    <div className="text-[8px] text-gray-400 font-medium pl-1">+{dayAppts.length - 3} weitere</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ==================== TEAM WEEK VIEW ====================
function TeamWeekView({ weekDays, teamData, userId, onEdit, onDragUpdate, onDragCopy, onNewAppt }) {
  const scrollRef = useRef(null);
  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = (7 - START_HOUR) * HOUR_HEIGHT; }, []);

  const members = teamData.length > 0 ? teamData : [];
  const memberCount = members.length || 1;

  // Colors for team members
  const MEMBER_COLORS = ["var(--tga-blue)", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981"];

  return (
    <div className="h-full flex flex-col border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid="team-calendar">
      {/* Header with day labels and member sub-columns */}
      <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50">
        <div className="flex">
          <div className="w-14 flex-shrink-0" />
          {weekDays.map((day, di) => {
            const td = isToday(day);
            return (
              <div key={di} className={`flex-1 border-l border-gray-200 ${td ? "bg-blue-50" : ""}`}>
                <div className="text-center py-1 border-b border-gray-100">
                  <span className="text-[9px] font-medium text-gray-500 uppercase">{DAY_LABELS[di]}</span>
                  <span className={`ml-1 text-[12px] font-bold ${td ? "text-blue-600" : "text-gray-800"}`}>{format(day, "d")}</span>
                </div>
                <div className="flex">
                  {members.map((m, mi) => (
                    <div key={mi} className="flex-1 text-center py-0.5 border-l border-gray-100 first:border-l-0">
                      <span className="text-[8px] font-semibold truncate block px-0.5" style={{ color: MEMBER_COLORS[mi % MEMBER_COLORS.length] }}>
                        {m.user_name.split(" ")[0]}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Grid */}
      <div ref={scrollRef} className="flex-1 overflow-auto">
        <div className="flex relative" style={{ height: TOTAL_HOURS * HOUR_HEIGHT, minWidth: members.length > 2 ? `${members.length * 7 * 80}px` : "100%" }}>
          {/* Hour labels */}
          <div className="w-14 flex-shrink-0 relative sticky left-0 bg-white z-10">
            {HOURS.map(h => (
              <div key={h} className="absolute left-0 right-0 border-t border-gray-100 text-[10px] text-gray-400 pr-2 text-right" style={{ top: (h - START_HOUR) * HOUR_HEIGHT }}>
                <span className="relative -top-[7px]">{`${String(h).padStart(2, "0")}:00`}</span>
              </div>
            ))}
          </div>

          {/* Day + member columns */}
          {weekDays.map((day, di) => (
            <div key={di} className="flex flex-1 border-l border-gray-300">
              {members.map((member, mi) => (
                <TimeGridColumn
                  key={`${di}-${mi}`}
                  dateStr={format(day, "yyyy-MM-dd")}
                  appts={member.appointments}
                  userId={userId}
                  onEdit={onEdit}
                  onDragUpdate={onDragUpdate}
                  onDragCopy={onDragCopy}
                  onNewAppt={onNewAppt}
                  scrollRef={scrollRef}
                  isTeam
                />
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex-shrink-0 flex items-center gap-4 px-3 py-1.5 border-t border-gray-200 bg-gray-50">
        {members.map((m, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: MEMBER_COLORS[i % MEMBER_COLORS.length] }} />
            <span className="text-[10px] font-medium text-gray-600">{m.user_name}</span>
            <span className="text-[9px] text-gray-400">({m.appointments.length})</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5 ml-auto">
          <div className="w-2.5 h-2.5 rounded-sm bg-blue-100 border border-blue-300" />
          <span className="text-[9px] text-gray-500">Geteilt</span>
          <div className="w-2.5 h-2.5 rounded-sm bg-red-100 border border-red-400 ml-2" />
          <span className="text-[9px] text-gray-500">Konflikt</span>
        </div>
      </div>
    </div>
  );
}

// ==================== YEAR CALENDAR VIEW ====================
function YearCalendarView({ currentDate, appointments, onDayClick, onMonthClick }) {
  const year = currentDate.getFullYear();
  const months = eachMonthOfInterval({ start: startOfYear(currentDate), end: endOfYear(currentDate) });

  const apptsByDate = {};
  appointments.forEach(a => {
    if (!apptsByDate[a.date]) apptsByDate[a.date] = 0;
    apptsByDate[a.date]++;
  });

  return (
    <div className="h-full overflow-y-auto" data-testid="year-calendar">
      <div className="grid grid-cols-3 lg:grid-cols-4 gap-4 p-2">
        {months.map((month, mi) => {
          const mStart = startOfMonth(month);
          const mEnd = endOfMonth(month);
          const days = eachDayOfInterval({ start: mStart, end: mEnd });
          const firstDow = (getDay(mStart) + 6) % 7;
          const pad = Array(firstDow).fill(null);
          const isCurrentMonth = isSameMonth(month, new Date());

          return (
            <div key={mi} className={`border rounded-lg overflow-hidden ${isCurrentMonth ? "border-blue-300 ring-1 ring-blue-100" : "border-gray-200"}`}>
              <button
                onClick={() => onMonthClick(month)}
                className={`w-full py-1.5 text-[12px] font-bold text-center border-b transition-colors ${isCurrentMonth ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-gray-100"}`}
                data-testid={`year-month-${mi}`}
              >
                {format(month, "MMMM", { locale: de })}
              </button>
              <div className="p-1">
                <div className="grid grid-cols-7 mb-0.5">
                  {["M", "D", "M", "D", "F", "S", "S"].map((d, i) => (
                    <div key={i} className="text-[7px] text-gray-400 text-center font-medium">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-px">
                  {pad.map((_, i) => <div key={`p${i}`} className="w-full aspect-square" />)}
                  {days.map((day, di) => {
                    const ds = format(day, "yyyy-MM-dd");
                    const count = apptsByDate[ds] || 0;
                    const td = isToday(day);
                    const intensity = count === 0 ? "" : count === 1 ? "bg-blue-100" : count === 2 ? "bg-blue-200" : count <= 4 ? "bg-blue-300" : "bg-blue-500";
                    return (
                      <button key={di}
                        onClick={(e) => { e.stopPropagation(); onDayClick(day); }}
                        className={`w-full aspect-square flex items-center justify-center text-[8px] rounded-sm transition-colors hover:ring-1 hover:ring-blue-400 ${
                          td ? "bg-blue-600 text-white font-bold" : count > 0 ? `${intensity} ${count > 2 ? "text-white" : "text-blue-900"} font-medium` : "text-gray-500 hover:bg-gray-100"
                        }`}
                        title={count > 0 ? `${count} Termin${count > 1 ? "e" : ""}` : ""}
                        data-testid={`year-day-${ds}`}
                      >
                        {format(day, "d")}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="flex items-center justify-center gap-3 py-3">
        <span className="text-[9px] text-gray-400">Terminanzahl:</span>
        <div className="flex items-center gap-1">
          {[{ label: "0", cls: "bg-gray-100" }, { label: "1", cls: "bg-blue-100" }, { label: "2", cls: "bg-blue-200" }, { label: "3-4", cls: "bg-blue-300" }, { label: "5+", cls: "bg-blue-500" }].map((l, i) => (
            <div key={i} className="flex items-center gap-0.5">
              <div className={`w-3 h-3 rounded-sm ${l.cls}`} />
              <span className="text-[8px] text-gray-500">{l.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==================== APPOINTMENT CARD ====================
function AppointmentCard({ appt, userId, onEdit, onDelete }) {
  const isOwn = appt.created_by === userId;
  const isShared = appt.visibility === "geteilt";
  return (
    <div className={`group relative bg-white rounded-lg border p-3 hover:shadow-md transition-all cursor-pointer ${appt.is_quick ? "border-l-4 border-l-amber-400 border-gray-200" : "border-gray-200"}`}
      onClick={onEdit} data-testid={`appointment-${appt.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {appt.time && <span className="text-[13px] font-bold text-slate-800 tabular-nums">{formatTime(appt.time)}{appt.end_time ? ` - ${formatTime(appt.end_time)}` : ""}</span>}
            {appt.is_quick && !appt.time && <span className="flex items-center gap-1 text-[11px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded"><Zap className="w-3 h-3" />Schnell</span>}
            {isShared ? <Share2 className="w-3 h-3 text-blue-500" /> : <Lock className="w-3 h-3 text-gray-300" />}
          </div>
          <p className="text-[14px] font-medium text-gray-900 truncate">{appt.title}</p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5">
            {appt.project_name && <span className="flex items-center gap-1 text-[11px] text-gray-500"><FolderOpen className="w-3 h-3" />{appt.project_name}</span>}
            {appt.location && <span className="flex items-center gap-1 text-[11px] text-gray-500"><MapPin className="w-3 h-3" />{appt.location}</span>}
            {appt.participants?.length > 0 && <span className="flex items-center gap-1 text-[11px] text-gray-500"><Users className="w-3 h-3" />{appt.participants.join(", ")}</span>}
          </div>
        </div>
        {isOwn && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
            <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="p-1.5 hover:bg-red-50 rounded"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== APPOINTMENT FORM ====================
function AppointmentForm({ appointment, projects, prefillData, onClose, onSave }) {
  const [form, setForm] = useState({
    title: appointment?.title || prefillData?.title || "",
    date: appointment?.date || prefillData?.date || format(new Date(), "yyyy-MM-dd"),
    time: appointment?.time || prefillData?.time || "",
    end_time: appointment?.end_time || "",
    project_id: appointment?.project_id || prefillData?.project_id || "",
    project_name: appointment?.project_name || "",
    participants: appointment?.participants?.join(", ") || "",
    note: appointment?.note || prefillData?.note || "",
    location: appointment?.location || "",
    visibility: appointment?.visibility || "privat",
  });
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    if (!form.date || !form.time) { setConflicts([]); return; }
    const check = async () => {
      const p = new URLSearchParams({ date: form.date, time: form.time });
      if (form.end_time) p.set("end_time", form.end_time);
      if (appointment?.id) p.set("exclude_id", appointment.id);
      try { const r = await authFetch(`${API}/appointments/conflicts?${p}`); if (r.ok) setConflicts(await r.json()); } catch {}
    };
    const t = setTimeout(check, 300);
    return () => clearTimeout(t);
  }, [form.date, form.time, form.end_time, appointment?.id]);

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Titel fehlt"); return; }
    setSaving(true);
    const payload = { ...form, participants: form.participants ? form.participants.split(",").map(s => s.trim()).filter(Boolean) : [] };
    if (form.project_id) { const proj = projects.find(p => p.id === form.project_id); if (proj) payload.project_name = proj.name; }
    try {
      const url = appointment?.id ? `${API}/appointments/${appointment.id}` : `${API}/appointments`;
      const res = await authFetch(url, { method: appointment?.id ? "PUT" : "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (res.ok) {
        const saved = await res.json().catch(() => ({}));
        toast.success(appointment?.id ? "Aktualisiert" : "Erstellt");
        onSave(saved?.id);
      }
      else { const e = await res.json(); toast.error(e.detail || "Fehler"); }
    } catch { toast.error("Fehler"); }
    finally { setSaving(false); }
  };

  const inp = "w-full px-3 py-2 text-[13px] border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-slate-400 bg-white";
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="appointment-form">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-[15px] font-semibold text-gray-900">{appointment?.id ? "Termin bearbeiten" : "Neuer Termin"}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5 text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-3">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Titel *" className={inp} autoFocus data-testid="form-title" />
          <div className="grid grid-cols-3 gap-2">
            <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className={inp} data-testid="form-date" />
            <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))} className={inp} data-testid="form-time" />
            <input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))} className={inp} data-testid="form-end-time" />
          </div>
          {conflicts.length > 0 && (
            <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg" data-testid="conflict-warning">
              <div className="flex items-center gap-1.5 text-[12px] font-medium text-red-700 mb-1"><AlertTriangle className="w-3.5 h-3.5" />Terminkonflikt!</div>
              {conflicts.map(c => <p key={c.id} className="text-[11px] text-red-600 pl-5">{c.time}{c.end_time ? `-${c.end_time}` : ""} {c.title}</p>)}
            </div>
          )}
          <select value={form.project_id} onChange={e => { const p = projects.find(x => x.id === e.target.value); setForm(f => ({ ...f, project_id: e.target.value, project_name: p?.name || "" })); }} className={inp} data-testid="form-project">
            <option value="">Projekt (optional)</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <input value={form.participants} onChange={e => setForm(f => ({ ...f, participants: e.target.value }))} placeholder="Teilnehmer (kommagetrennt)" className={inp} data-testid="form-participants" />
          <input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} placeholder="Ort" className={inp} data-testid="form-location" />
          <textarea value={form.note} onChange={e => setForm(f => ({ ...f, note: e.target.value }))} placeholder="Notiz" rows={2} className={inp} data-testid="form-note" />
          <div className="flex items-center gap-3" data-testid="form-visibility">
            <button onClick={() => setForm(f => ({ ...f, visibility: "privat" }))} className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-lg border transition-colors ${form.visibility === "privat" ? "bg-gray-100 border-gray-300 text-gray-800" : "border-gray-200 text-gray-400"}`}><Lock className="w-3 h-3" />Privat</button>
            <button onClick={() => setForm(f => ({ ...f, visibility: "geteilt" }))} className={`flex items-center gap-1.5 px-3 py-1.5 text-[12px] font-medium rounded-lg border transition-colors ${form.visibility === "geteilt" ? "bg-blue-50 border-blue-300 text-blue-700" : "border-gray-200 text-gray-400"}`}><Share2 className="w-3 h-3" />Geteilt</button>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2 text-[13px] font-medium text-gray-500 hover:text-gray-700">Abbrechen</button>
          <button onClick={handleSave} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-medium bg-slate-800 text-white rounded-lg hover:bg-slate-900 disabled:opacity-50" data-testid="form-save-btn">
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}{appointment?.id ? "Speichern" : "Erstellen"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ==================== VOICE BUTTON ====================
function VoiceButton({ onResult }) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mediaRec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 100) { toast.error("Keine Aufnahme"); return; }
        setProcessing(true);
        try {
          const fd = new FormData(); fd.append("audio", blob, "rec.webm");
          const res = await authFetch(`${API}/appointments/voice`, { method: "POST", body: fd });
          if (!res.ok) { toast.error("Fehler"); setProcessing(false); return; }
          const data = await res.json();
          toast.success(`Erkannt: "${data.transcript}"`);
          const payload = { title: data.title || data.transcript, date: data.date || format(new Date(), "yyyy-MM-dd"), time: data.time || null, end_time: data.end_time || null, project_id: data.project_id || "", project_name: data.project_name || "", participants: data.participants || [], location: data.location || "", note: data.note || "", visibility: "privat", is_quick: data.is_quick || !data.time };
          const cr = await authFetch(`${API}/appointments`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
          if (cr.ok) { toast.success("Termin erstellt"); onResult(); }
        } catch { toast.error("Fehler"); }
        finally { setProcessing(false); }
      };
      mediaRec.start();
      mediaRecRef.current = mediaRec;
      setRecording(true);
    } catch { toast.error("Mikrofon nicht verfuegbar"); }
  };
  const stopRecording = () => { if (mediaRecRef.current?.state !== "inactive") mediaRecRef.current?.stop(); setRecording(false); };

  if (processing) return <button disabled className="flex items-center gap-1.5 px-3 py-2 bg-purple-100 text-purple-700 text-[13px] font-medium rounded-lg"><Loader2 className="w-4 h-4 animate-spin" />KI...</button>;
  return recording ? (
    <button onClick={stopRecording} className="flex items-center gap-1.5 px-3 py-2 bg-red-500 text-white text-[13px] font-medium rounded-lg animate-pulse" data-testid="voice-stop-btn"><MicOff className="w-4 h-4" />Stopp</button>
  ) : (
    <button onClick={startRecording} className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white text-[13px] font-medium rounded-lg hover:bg-purple-700" data-testid="voice-start-btn"><Mic className="w-4 h-4" />Sprechen</button>
  );
}



// ═══════════════════════════════════════════
// QuickTaskModal — Schnellerfassung Aufgabe (<3s)
// ═══════════════════════════════════════════
function QuickTaskModal({ projects, onClose, onSaved }) {
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState(new Date().toISOString().slice(0, 10));
  const [category, setCategory] = useState("privat");
  const [projectId, setProjectId] = useState("");
  const [priority, setPriority] = useState("normal");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!title.trim()) { toast.error("Titel fehlt"); return; }
    setSaving(true);
    try {
      const body = {
        title: title.trim(),
        due_date: dueDate ? `${dueDate}T08:00:00` : null,
        category,
        project_id: projectId || null,
        priority,
        visibility: "privat",
      };
      const res = await authFetch(`${API}/personal-tasks`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) { toast.success("Aufgabe angelegt"); onSaved(); }
      else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Fehler beim Speichern");
      }
    } catch { toast.error("Netzwerkfehler"); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={onClose} data-testid="quick-task-modal">
      <div className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{ background: "var(--tga-bg-elevated)", border: "1px solid var(--tga-border-strong)" }}
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--tga-border)" }}>
          <p className="text-base font-bold" style={{ color: "var(--tga-text)" }}>Aufgabe schnell anlegen</p>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-white/[0.04]" data-testid="quick-task-close">
            <X className="w-4 h-4" style={{ color: "var(--tga-text-dim)" }} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold block mb-1" style={{ color: "var(--tga-text-secondary)" }}>Titel *</label>
            <input autoFocus value={title} onChange={e => setTitle(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && title.trim()) submit(); }}
              placeholder="z.B. Material bestellen, Kunde anrufen..."
              className="w-full h-10 px-3 rounded-lg text-sm outline-none"
              style={{ background: "var(--tga-bg-content)", border: "1px solid var(--tga-border)", color: "var(--tga-text)" }}
              data-testid="quick-task-title" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold block mb-1" style={{ color: "var(--tga-text-secondary)" }}>Faellig</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                className="w-full h-10 px-2 rounded-lg text-sm outline-none"
                style={{ background: "var(--tga-bg-content)", border: "1px solid var(--tga-border)", color: "var(--tga-text)" }}
                data-testid="quick-task-due" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold block mb-1" style={{ color: "var(--tga-text-secondary)" }}>Kategorie</label>
              <select value={category} onChange={e => setCategory(e.target.value)}
                className="w-full h-10 px-2 rounded-lg text-sm outline-none"
                style={{ background: "var(--tga-bg-content)", border: "1px solid var(--tga-border)", color: "var(--tga-text)" }}
                data-testid="quick-task-category">
                <option value="privat">Privat</option>
                <option value="buero">Büro</option>
                <option value="projekt">Projekt</option>
              </select>
            </div>
          </div>
          {category === "projekt" && (
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold block mb-1" style={{ color: "var(--tga-text-secondary)" }}>Projekt</label>
              <select value={projectId} onChange={e => setProjectId(e.target.value)}
                className="w-full h-10 px-2 rounded-lg text-sm outline-none"
                style={{ background: "var(--tga-bg-content)", border: "1px solid var(--tga-border)", color: "var(--tga-text)" }}
                data-testid="quick-task-project">
                <option value="">– Kein Projekt –</option>
                {(projects || []).map(p => <option key={p.id} value={p.id}>{p.title || p.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold block mb-1" style={{ color: "var(--tga-text-secondary)" }}>Prioritaet</label>
            <div className="flex gap-1">
              {[{k:"niedrig",l:"Niedrig"},{k:"normal",l:"Normal"},{k:"hoch",l:"Hoch"},{k:"kritisch",l:"Kritisch"}].map(p => (
                <button key={p.k} onClick={() => setPriority(p.k)}
                  className={`flex-1 h-9 rounded-lg text-[11px] font-semibold transition-colors ${priority === p.k ? "bg-blue-500/20 border border-blue-500/40 text-blue-300" : ""}`}
                  style={priority !== p.k ? { background: "var(--tga-bg-content)", border: "1px solid var(--tga-border)", color: "var(--tga-text-secondary)" } : {}}
                  data-testid={`quick-task-prio-${p.k}`}>
                  {p.l}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 p-4 border-t" style={{ borderColor: "var(--tga-border)" }}>
          <button onClick={onClose} className="flex-1 h-10 rounded-lg text-sm font-semibold"
            style={{ background: "var(--tga-bg-content)", border: "1px solid var(--tga-border)", color: "var(--tga-text-secondary)" }}
            data-testid="quick-task-cancel">Abbrechen</button>
          <button onClick={submit} disabled={!title.trim() || saving}
            className="flex-1 h-10 rounded-lg text-sm font-bold bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-40 flex items-center justify-center gap-1.5"
            data-testid="quick-task-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Aufgabe anlegen
          </button>
        </div>
      </div>
    </div>
  );
}
